/***************************
@avantia common-code

This file exists in order that functions/features can be shared
between questionSetPayloadMapper and questionSetReducerLibrary
************************* */
import {
  basicSorter,
  ensureDefined,
  HpSortFunctionResult,
  MicroTimer,
  rangeWorker,
  SMap
} from '@avantia/client-and-server-utilities';
import { HpField, HpFields, validateFields } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { hasOwnProperty, HpEither, HpError } from '@avantia/logging-and-errors';
import {
  createControlId,
  destructureControlId,
  getIdentifierConfigDefaultValues,
  HpAnswer,
  HpAnswerMap,
  HpButtonTextAndStyle,
  HpCommonButtonTextAndStyle,
  HpCreateControlIdOptions,
  HpDestructuredControlIdResult,
  HpFluentButtonTextAndStyle,
  HpFluentInputTextAndStyle,
  HpHelpInfo,
  HpHelpInfoItem,
  HpInputTextAndStyle,
  HpQuestionFormatTypes,
  HpQuestionMap,
  HpQuestionOrderTypes,
  HpSection,
  HpSectionMap,
  HpStandardButtonTextAndStyle,
  HpStandardInputTextAndStyle,
  HpTokensAndExpressionsThisReference,
  identifierConfigChanged
} from '@avantia/questionset-model';
import { HpQuestionType } from '@avantia/questionset-script-parser/types';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { displayTimeTaken, getDebugFlags, logInfo } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getCustomExtension } from '../customExtensionRegistry';
import { HpSortQuestionIdNodesUsingListExtensionFunction } from '../customExtensionRegistryTypes';
import { getMetaDataOrUndefinedFromAnswers, HpMetaData } from '../tools/clientAndServerUtilities';
import { HpClientPrerequisiteThisReferenceClass } from '../tools/prerequisiteThisReference';
import {
  forEachRepeaterSection,
  forEachSectionInOrder,
  getNextOrPreviousSectionId
} from '../tools/questionDisplayStateTools';
import {
  evaluateCalculatedAnswer,
  evaluateLookupConditions,
  HpConditionMetFunction,
  performActionOnConditionalModels,
  questionPrerequisitesHaveBeenMet,
  sectionPrerequisitesHaveBeenMet
} from '../tools/questionPrerequisites';
import { answersAreSame } from '../tools/utilities';
import {
  HpClientAnswerMap,
  HpClientConditionFunction,
  HpClientHelpInfoConditionalText,
  HpClientQuestion,
  HpClientQuestionData,
  HpClientQuestionLookupItemMap,
  HpClientQuestionMap,
  HpClientQuestionSetState,
  HpClientQuestionTextConditionalText,
  HpClientQuestionUI,
  HpClientQuestionUiMap,
  HpClientSection,
  HpClientSectionMap,
  HpClientSectionsUiMap,
  HpClientSectionUI,
  HpClientValidationResult,
  HpEventTypes,
  HpHelpInfoUI,
  HpQuestionBasic,
  HpQuestionDisplayState,
  HpQuestionIdGroup,
  HpQuestionIdNode,
  HpSectionBasic
} from '../types';
import { HpInfoPositionType } from '../types/widgetUtilitiesTypes';
import { createValidationModel } from './questionSetReducerLibrary';
import { HpVisibleQuestionCustomization } from './questionSetToolsCustomizationInterface';
import { applyChanges } from './reducerLibrary';

export function createBlankAnswer(flag: HpClientDebugFlags): HpAnswer {
  logInfo(`Created blank answer for ${getDebugFlags(flag)[0]}`, HpClientDebugFlags.BlankAnswers);
  return { customer: null, aggregator: null, default: null };
}

export type HpMoveToSectionTypes = 'next-section' | 'previous-section' | 'move-to-section' | 'validate-fields';

export type HpNavigationActionTypes = 'show' | 'hide' | 'select' | 'not-allowed';

export type HpNavigationBarElementTypes = 'menu' | 'submenu';

interface HpCanMoveToDesiredSectionProps {
  buttonAction: HpMoveToSectionTypes;
  bypassMoveToSectionCheck?: true; // most times, this should not be provided.
  currentSectionId: string;
  desiredSectionId: string | undefined;
  displayState: HpQuestionDisplayState;
  state: HpClientQuestionSetState;
  validationResult: HpClientValidationResult;
}

interface HpCanMoveToDesiredSectionResult {
  moveToSectionId: string;
  moveToSectionPosition: number;
  validationResult: HpClientValidationResult;
}

export interface HpIterateVisibleQuestionsProps<QuestionT extends HpQuestionBasic, SectionT extends HpSectionBasic> {
  action: HpIterateVisibleQuestionAction<QuestionT>;
  questions: SMap<QuestionT>;
  sections: SMap<SectionT>;
  questionsUI: HpClientQuestionUiMap;
  section: HpSection | HpClientSection | undefined;
}

export type HpIterateVisibleQuestionAction<QuestionT extends HpQuestionBasic> = (props: {
  question: QuestionT;
  questionId: string;
}) => void;

const noPrefix: Readonly<HpCreateControlIdOptions> = { noPrefix: true };
const helpInfoUiKeys: (keyof HpHelpInfoUI)[] = [
  'canShowHelp',
  'canShowInfo',
  'canShowWarning',
  'helpText',
  'infoPosition',
  'infoText',
  'isHelpVisible',
  'warningText',
  'isInfoIconHidden'
];

let validateOnBackNavigation = true;
let { maxGroupsAllowed, groupStartNumber, questionOrderPropertyIsString } = getIdentifierConfigDefaultValues(); // just a temp default, it will be set correctly below.

registerConfigurationAction((config) => {
  ({ validateOnBackNavigation = true } = config);
});

identifierConfigChanged(({ maxGroupsAllowed: mga, groupStartNumber: gsn, questionOrderPropertyIsString: qopis }) => {
  maxGroupsAllowed = mga;
  groupStartNumber = gsn;
  questionOrderPropertyIsString = qopis;
});

export function iterateVisibleQuestions<QuestionT extends HpQuestionBasic, SectionT extends HpSectionBasic>({
  action,
  questions,
  sections,
  questionsUI,
  section
}: HpIterateVisibleQuestionsProps<QuestionT, SectionT>): void {
  const { sectionId, questionIds } = section || {};

  for (const qid in questions) {
    const question = questions[qid];
    const { groupId, sectionId: qSectionId, visible } = question;
    const questionUI = questionsUI[qid];

    if (visible !== false && questionUI && questionUI.isVisible) {
      const group = groupId ? sections[groupId] : undefined;
      let isTemplateQuestion = false;
      if (group) {
        if (group.isRepeater === true) {
          const { groupNumber } = destructureControlId(qid, { requirePrefix: false });
          if (groupNumber === undefined) {
            isTemplateQuestion = true;
          }
        }
      }

      if (!isTemplateQuestion) {
        if (questionIds) {
          if (questionIds.includes(qid)) {
            action({ question, questionId: qid });
          }
        } else if (!sectionId || sectionId === qSectionId) {
          action({ question, questionId: qid });
        }
      }
    }
  }
}

export function iterateInvisibleQuestions(
  questions: HpClientQuestionMap,
  questionsUI: HpClientQuestionUiMap,
  sectionId: string,
  action: (props: { question: HpClientQuestion; questionId: string }) => void
): void {
  for (const id in questions) {
    const question = questions[id];
    const questionUI = questionsUI[id];
    if (questionUI && !questionUI.isVisible && (!sectionId || sectionId === question.sectionId)) {
      action({ question, questionId: id });
    }
  }
}

export function getActiveSection(displayState: HpQuestionDisplayState): HpClientSection {
  const { activeSectionId, sections } = displayState;
  const section = sections[activeSectionId];
  if (!section) {
    throw new Error(`There is no active section.`);
  }

  return section;
}

export interface HpDetermineVisibleQuestionsProps {
  activeSection: HpSection | HpClientSection;
  customization: HpVisibleQuestionCustomization;
  questions: HpClientQuestionMap;
  questionsUI: HpClientQuestionUiMap;
  sections: HpClientSectionMap;
}

export interface HpVisibleQuestionItem {
  groupId?: string | undefined;
  parentId?: string | undefined;
  questionId: string;
}

export function determineVisibleQuestions({
  questions,
  activeSection,
  questionsUI,
  sections,
  customization
}: HpDetermineVisibleQuestionsProps): HpQuestionIdNode[] {
  if (!activeSection) {
    throw new Error('The activeSection argument must be provided');
  }

  // The questionsUI initially has questions in repeating groups with visible=false, and everything else with visible=true.
  // The visibleQuestionList array will contain only visible questions for the specified section.
  const visibleQuestionList: HpVisibleQuestionItem[] = [];
  iterateVisibleQuestions({
    questions,
    sections,
    questionsUI,
    section: activeSection,
    action: ({ question }) => {
      const { groupId, questionId } = question;
      visibleQuestionList.push({ groupId, questionId });
    }
  });

  let visibleQuestions: HpQuestionIdNode[] = createQuestionTreeForDisplay(visibleQuestionList, sections, customization);

  // We will always have visibleQuestions populated, except in some unit tests.
  if (visibleQuestions.length > 0) {
    const { questionIds } = activeSection;
    if (questionIds) {
      const sortQuestionIdNodesUsingListHandler =
        getCustomExtension<HpSortQuestionIdNodesUsingListExtensionFunction>('sortQuestionIdNodesUsingList') ||
        sortQuestionIdNodesUsingList;

      visibleQuestions = sortQuestionIdNodesUsingListHandler(visibleQuestions, questionIds);
    } else {
      sortQuestionIdNodes(visibleQuestions, (id) => questions[id]);
    }
  }

  return visibleQuestions;
}

interface HpAddChildToParentProps {
  child: HpQuestionIdGroup;
  customization: HpVisibleQuestionCustomization;
  indexes: number[] | undefined;
  leafId: string[];
  level: number;
  parentId: string;
}

/**
 * Creates a tree structure of the questions that are to be displayed.
 * @param visibleQuestionList all questions that are visible in the current section.
 * @param sections a map of all sections.
 * @returns a nested tree structure.
 */
export function createQuestionTreeForDisplay(
  visibleQuestionList: HpVisibleQuestionItem[],
  sections: SMap<HpSectionBasic>,
  customization: HpVisibleQuestionCustomization
): HpQuestionIdNode[] {
  const questionIdNodes: HpQuestionIdNode[] = [];
  const nodeMap: SMap<HpQuestionIdGroup> = {};
  const added: SMap<true> = {};
  const { ignoreRepeaterWithNoGroup } = customization;

  // The questionId may contain a group number
  visibleQuestionList.forEach(({ groupId, questionId }) => {
    if (!groupId) {
      questionIdNodes.push(questionId);
      return;
    }

    addQuestionToTree(questionId, groupId);
  });
  return questionIdNodes;

  // private functions below
  function addQuestionToTree(questionId: string, groupId: string) {
    // eslint-disable-next-line prefer-const
    let { groupNumber, indexes = [] } = destructureControlId(questionId, { requirePrefix: false });
    if (groupNumber !== undefined) {
      indexes.push(groupNumber);
    }

    const {
      indexes: ix,
      parts,
      groupNumber: grpNo
    } = customization.removeEnding({
      indexes,
      parts: customization.splitQuestionId(questionId),
      childGroupKey: questionId,
      forParent: false
    });

    const group = sections[groupId];
    if (ignoreRepeaterWithNoGroup && group.isRepeater && grpNo === undefined) {
      return; // this is the template question, which is never displayed
    }

    const groupKey = createControlId({ questionId: groupId, indexes: ix, options: noPrefix });
    let child = nodeMap[groupKey];
    const exists = !!child;
    if (!exists) {
      child = {
        groupId,
        groupKey,
        groupNumber: grpNo,
        questions: []
      };
      nodeMap[groupKey] = child;
    }

    child.questions.push(questionId);

    if (exists) {
      return;
    }

    const parentId = ensureDefined(sections[groupId], `sections['${groupId}']`).parentId;
    if (parentId) {
      const props: HpAddChildToParentProps = {
        parentId,
        child,
        indexes: ix,
        leafId: parts,
        level: 0,
        customization
      };
      addChildToParent(props);
    } else {
      if (!added[groupKey]) {
        questionIdNodes.push(child);
        added[groupKey] = true;
      }
    }
  }

  function addChildToParent(props: HpAddChildToParentProps) {
    const { child, customization, indexes, leafId, level, parentId } = props;
    const { groupKey: childGroupKey = '' } = child;
    const parentSection = sections[parentId];

    if (parentId === child.groupId || !parentSection || !parentSection.isRepeater) {
      if (!added[childGroupKey]) {
        questionIdNodes.push(child);
        added[childGroupKey] = true;
      }

      return;
    }

    const {
      indexes: ix,
      parts: bits,
      groupNumber: parentNumber
    } = customization.removeEnding({ indexes, parts: leafId, childGroupKey, forParent: true });
    const parentKey = createControlId({ questionId: parentId, indexes: ix, options: noPrefix });

    let parent = nodeMap[parentKey];
    if (!parent) {
      parent = { groupId: parentId, groupNumber: parentNumber, groupKey: parentKey, questions: [] };
      nodeMap[parentKey] = parent;
    }

    if (!added[childGroupKey]) {
      parent.questions.push(child);
      added[childGroupKey] = true;

      if (!parentId && !added[parentKey]) {
        questionIdNodes.push(parent);
        added[parentKey] = true;
      }

      if (parentId && parentSection.isRepeater) {
        const childProps: HpAddChildToParentProps = {
          parentId,
          child: parent,
          indexes: ix,
          leafId: bits,
          level: level + 1,
          customization
        };
        addChildToParent(childProps);
      }
    }
  }
}

export type HpGetItemWithOrderAndPositionPropsFunction = (questionId: string) => HpWithOrderAndPosition;

export function sortQuestionIdNodes(
  questionIdNodes: HpQuestionIdNode[],
  getQuestionById: HpGetItemWithOrderAndPositionPropsFunction
): void {
  const sortFunc: (idA: HpQuestionIdNode, idB: HpQuestionIdNode) => number = (
    idA: HpQuestionIdNode,
    idB: HpQuestionIdNode
  ) => {
    const getQ: (id: HpQuestionIdNode) => HpWithOrderAndPosition = (id) => {
      if (typeof id === 'object') {
        const g = id as HpQuestionIdGroup;
        if (g.questions && g.questions.length > 0) {
          const firstQuestionsItem = g.questions[0];
          if (typeof firstQuestionsItem === 'string') {
            return getQuestionById(firstQuestionsItem);
          } else {
            // recursively get the first questionId in the group.
            return getQ(firstQuestionsItem);
          }
        }

        throw new Error(`There are no questions in group "${g.groupId}".`);
      }

      return getQuestionById(id as string);
    };

    return questionOrderSorter(getQ(idA), getQ(idB));
  };

  for (const grp of questionIdNodes) {
    if (typeof grp === 'object') {
      grp.questions.sort(sortFunc);
    }
  }

  questionIdNodes.sort(sortFunc);
}

/**
 * Sorts the *visibleQuestions* according to the order of *questionIds*.
 * This is required when the *section* contains an explicit list of *questionIds* (e.g. quotePage, agentContactDetails).
 * @param visibleQuestions The visible questions.
 * @param questionIds The order (as specified in the quesionSet)
 * @returns Visible questions in the order of *questionIds*.
 */
export function sortQuestionIdNodesUsingList(
  visibleQuestions: HpQuestionIdNode[],
  questionIds: string[]
): HpQuestionIdNode[] {
  const idToIndexMap: SMap<number> = {};
  visibleQuestions.forEach((vq, index) => {
    if (typeof vq === 'string') {
      idToIndexMap[vq] = index;
    } else {
      addIdToMap(index, vq);
    }
  });

  let list: HpQuestionIdNode[] = [];
  const seen: SMap<true> = {}; // we don't want to add a group twice.
  let buttonGroupIndex: number | undefined;
  questionIds.forEach((questionId) => {
    const ix = idToIndexMap[questionId];
    if (ix !== undefined) {
      const node = visibleQuestions[ix];
      if (typeof node === 'string') {
        list.push(node);
      } else if (!seen[questionId]) {
        const { groupId, questions } = node;
        if (groupId === 'button-grouping' && buttonGroupIndex === undefined) {
          buttonGroupIndex = list.length;
        }

        questions.forEach((id) => {
          if (typeof id === 'string') {
            seen[id] = true;
          }
        });
        list.push(node);
      }
    }
  });

  // Move the 'button-grouping' node to the end.
  if (buttonGroupIndex !== undefined) {
    const buttonGroup = list[buttonGroupIndex];
    list = list.slice(0, buttonGroupIndex).concat(list.slice(buttonGroupIndex + 1));
    list.push(buttonGroup);
  }

  return list;

  function addIdToMap(index: number, vq: string | HpQuestionIdNode): void {
    if (typeof vq === 'string') {
      idToIndexMap[vq] = index;
    } else {
      vq.questions.forEach((idOrGroupNode) => {
        if (typeof idOrGroupNode === 'string') {
          idToIndexMap[idOrGroupNode] = index;
        } else {
          addIdToMap(index, idOrGroupNode);
        }
      });
    }
  }
}

export function createQuestionIdSorter(
  questions: HpClientQuestionMap
): (q1Id: string, q2Id: string) => HpSortFunctionResult {
  return (q1Id, q2Id): HpSortFunctionResult => {
    const q1 = questions[q1Id];
    const q2 = questions[q2Id];
    return questionOrderSorter(q1, q2);
  };
}

export interface HpWithOrderAndPosition {
  order: HpQuestionOrderTypes;
  position: number;
}

export function questionOrderSorter(a: HpWithOrderAndPosition, b: HpWithOrderAndPosition): HpSortFunctionResult {
  let ao: HpQuestionOrderTypes, bo: HpQuestionOrderTypes;
  if (!questionOrderPropertyIsString) {
    const notProvided = 9999999999;
    ao = (a.order as number) >= 0 ? a.order : notProvided; // a: child-first-name~G0101
    bo = (b.order as number) >= 0 ? b.order : notProvided; // b: has-pets~G01
  } else {
    const notProvided = '~~~~'; // tilda code is 126, so this will appear last
    ao = (a.order as string) || notProvided;
    bo = (b.order as string) || notProvided;
  }

  let res = basicSorter(ao, bo);
  if (res === 0) {
    ao = a.position;
    bo = b.position;
    res = basicSorter(ao, bo);
  }

  return res;
}

function questionChangesIterator<ItemT>(
  description: string,
  questions: HpClientQuestionMap,
  allOrderedQuestionIds: string[],
  modifier: (q: HpClientQuestion, index: number) => ItemT | undefined
): { modified: SMap<ItemT>; count: number } {
  const timer = new MicroTimer().start();
  const modified: SMap<ItemT> = {};
  let count = 0;
  let index = 0;
  for (const questionId of allOrderedQuestionIds) {
    const question = questions[questionId];
    if (!question) {
      throw new Error(`There is no question with id "${questionId}".`);
    }

    const changed = modifier(question, index++);
    if (changed) {
      modified[questionId] = changed;
      count++;
    }
  }

  displayTimeTaken(`${description} (${count} changes)`, timer);
  return { modified, count };
}

export function evaluateSectionPrerequisites(
  displayState: HpQuestionDisplayState,
  state: HpClientQuestionSetState,
  eventType: HpEventTypes
): SMap<HpClientSectionUI> {
  const sectionsUI: SMap<HpClientSectionUI> = { ...displayState.sectionsUI };
  forEachSectionInOrder({
    displayState,
    options: { iterate: 'sections-only' },
    worker: ({ sectionUI, sectionId }) => {
      const isVisible = sectionPrerequisitesHaveBeenMet(sectionId, eventType, state);
      sectionsUI[sectionId] = applyChanges(sectionUI, { isVisible });
    }
  });

  processRepeaterSectionHelpInfoConditions({ displayState, state, sectionsUI });

  return sectionsUI;
}

export interface HpProcessRepeaterSectionHelpInfoConditionsProps {
  displayState: HpQuestionDisplayState;
  sectionsUI: SMap<HpClientSectionUI>;
  state: HpClientQuestionSetState;
}

/**
 * Process repeater section help and info text
 * @param props sectionsUI will be mutated. Be sure to clone it beforehand if this is not what you need.
 */
export function processRepeaterSectionHelpInfoConditions(props: HpProcessRepeaterSectionHelpInfoConditionsProps): void {
  const { displayState, sectionsUI, state } = props;
  const { answers } = state;
  const { sections: displayStateSections } = displayState;
  forEachRepeaterSection({
    displayState,
    worker: (section, ui, sectionId) => {
      const { helpInfo } = section;
      if (helpInfo) {
        rangeWorker(groupStartNumber, maxGroupsAllowed, (sectionGroupNumber) => {
          const firstQuestionId = createControlId({
            questionId: ensureDefined(
              displayStateSections[sectionId].questionIds,
              'displayStateSection.questionIds'
            )[0],
            groupNumber: sectionGroupNumber,
            options: noPrefix
          });

          if (!hasOwnProperty(answers, firstQuestionId)) {
            // don't loop up to MaxGroupsAllowed if there are no answers
            return false;
          }

          const updatedUi = helpInfoConditionsHaveBeenMet({
            helpInfo,
            questionId: sectionId,
            questionUI: ui as HpClientQuestionUI,
            state,
            sectionGroupNumber
          });

          if (updatedUi) {
            const sid = createControlId({
              questionId: sectionId,
              groupNumber: sectionGroupNumber,
              options: noPrefix
            });
            sectionsUI[sid] = applyChanges(ui, updatedUi);
          }

          return true;
        });
      }
    }
  });
}

export function evaluateCalculatedAnswers(
  state: HpClientQuestionSetState,
  eventType: HpEventTypes
): HpClientQuestionSetState {
  const { answers, displayState, questions } = state;
  const { modified, count } = questionChangesIterator<HpAnswer>(
    'Evaluate calculated answers',
    questions,
    displayState.allOrderedQuestionIds,
    (question) => {
      const { calculatedAnswer, dataType, groupId, questionId } = question;
      if (!calculatedAnswer) {
        return undefined;
      }

      if (groupId && displayState.sections[groupId].isRepeater) {
        if (!destructureControlId(questionId, { requirePrefix: false }).groupNumber) {
          return undefined;
        }
      }

      const answerValue = evaluateCalculatedAnswer(question, state, eventType);
      const answer = answers[questionId];
      if (answer && answersAreSame(dataType, answer.customer, answerValue)) {
        return undefined;
      }

      return applyChanges(answer || createBlankAnswer(HpClientDebugFlags.CalculatedAnswers), {
        customer: answerValue
      });
    }
  );

  if (count) {
    const changedAnswers = applyChanges(answers, modified);
    state = applyChanges(state, { answers: changedAnswers });
  }

  return state;
}

export function evaluateLookupExpressions(state: HpClientQuestionSetState): HpClientQuestionSetState {
  // eslint-disable-next-line prefer-const
  let { answers, commonLookups, displayState, productId, questions } = state;
  const { sections } = displayState;
  let count = 0;
  const changedData: SMap<HpClientQuestionLookupItemMap> = {};

  const self = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag: HpClientDebugFlags.LookupExpressions,
    displayState,
    groupNumber: undefined,
    indexes: undefined,
    productId,
    questions,
    sections,
    sourceQuestionId: '',
    sourceQuestionType: undefined
  });

  lodash.forOwn(questions, ({ questionId, data }) => {
    const qdata = data as HpClientQuestionData;
    if (qdata.lookupExpr) {
      const lookup = qdata.lookupExpr.call(self, questionId);
      if (!lookup || typeof lookup !== 'object') {
        throw new Error(
          `The function should return a lookup map (object), but instead returned ${typeof lookup}.\n${
            qdata.lookupExpr
          }`
        );
      }

      // Don't update state unless the objects are different
      if (!qdata.lookup || !lodash.isEqual(qdata.lookup, lookup)) {
        changedData[questionId] = lookup;
        count++;
      }
    }
  });

  if (count > 0) {
    questions = { ...questions };
    lodash.forOwn(changedData, (lookup, key) => {
      const question = questions[key];
      const data = { ...question.data } as HpClientQuestionData;
      data.lookup = lookup;
      questions[key] = applyChanges(question, { data });
    });
    state = applyChanges(state, { questions });
  }

  return state;
}

export function evaluateQuestionPrerequisites(
  state: HpClientQuestionSetState,
  eventType: HpEventTypes
): HpClientQuestionSetState {
  const { displayState, questions } = state;
  const questionsUI = { ...displayState.questionsUI };
  const { count } = questionChangesIterator<boolean>(
    'Evaluate question prerequisites',
    questions,
    displayState.allOrderedQuestionIds,
    (question, index) => evaluateQuestionPrerequisite({ question, eventType, state, questionsUI, index })
  );

  if (count) {
    const changedDisplayState = applyChanges(displayState, { questionsUI });
    state = applyChanges(state, { displayState: changedDisplayState });
  }

  return state;
}

export interface HpEvaluateQuestionPrerequisiteProps {
  eventType: HpEventTypes;
  index: number;
  question: HpClientQuestion;
  questionsUI: HpClientQuestionUiMap;
  state: HpClientQuestionSetState;
}

export function evaluateQuestionPrerequisite({
  eventType,
  question,
  questionsUI,
  state
}: HpEvaluateQuestionPrerequisiteProps): boolean {
  const { questionId, data } = ensureDefined(question, 'question');
  // return recordElapsedTime(`${index + 1}) evaluateQuestionPrerequisite[${questionId}]`, () => {
  const { questions } = state;
  let changeCount = 0;
  const { lookup, helpInfo } = data as HpClientQuestionData;
  if (
    evaluateQuestionPrerequisiteInner(
      questionId,
      questions[questionId],
      eventType,
      helpInfo,
      lookup,
      questionsUI,
      state
    )
  ) {
    changeCount++;
  }

  return changeCount > 0;
  // });
}

function evaluateQuestionPrerequisiteInner(
  questionId: string,
  question: HpClientQuestion,
  eventType: HpEventTypes,
  helpInfo: HpHelpInfo | undefined,
  lookup: HpClientQuestionLookupItemMap,
  questionsUI: HpClientQuestionUiMap,
  state: HpClientQuestionSetState
): boolean {
  let isChanged = false;
  const met = questionPrerequisitesHaveBeenMet(questionId, eventType, state);
  let questionUI = questionsUI[questionId];
  if (!questionUI || met !== questionUI.isVisible) {
    questionsUI[questionId] = applyChanges(questionUI, { isVisible: met });
    isChanged = true;
  }

  if (helpInfo) {
    const quiChanged = helpInfoConditionsHaveBeenMet({
      helpInfo,
      questionId,
      questionUI: questionsUI[questionId],
      state
    });

    if (quiChanged) {
      const prevQuestionsUI = questionsUI[questionId];
      questionsUI[questionId] = { ...prevQuestionsUI, ...quiChanged };
      isChanged = true;
    }
  }

  const orig = questionsUI[questionId];
  const questionText = evaluateConditionalQuestionText({ question, state });

  if (questionText) {
    questionsUI[questionId] = applyChanges(questionsUI[questionId], { ...questionText });
    isChanged = true;
  }

  const disabled = evaluateDisabledExpression({ question, state });
  if (disabled !== undefined && orig.disabled !== disabled) {
    questionsUI[questionId] = applyChanges(questionsUI[questionId], { disabled });
    isChanged = true;
  }

  questionUI = questionsUI[questionId];
  if (lookup && questionUI.lookupVisibility !== false) {
    const { visible: lookupVisibility, count } = evaluateLookupConditions(questionId, eventType, lookup, state);
    if (count > 0) {
      questionsUI[questionId] = applyChanges(questionUI, { lookupVisibility });
    } else {
      questionsUI[questionId] = applyChanges(questionUI, { lookupVisibility: false });
    }

    isChanged = true;
  }

  return isChanged;
}

export interface HpQuestionTextResult {
  questionText: string;
  subtext: string | undefined;
}

export interface HpButtonTextResult {
  buttonText: string;
  subtext: string | undefined;
}

export interface HpGetQuestionTextProps {
  question: HpClientQuestion;
  questionFormats: HpQuestionFormatTypes[];
  questionUI: HpClientQuestionUI;
}

export function getQuestionText({
  question,
  questionFormats,
  questionUI
}: HpGetQuestionTextProps): HpEither<HpQuestionTextResult, HpButtonTextResult> {
  const { data } = question;
  const textAndStyle = selectTextAndStyleDataToUse(
    questionFormats,
    data.textAndStyle as HpButtonTextAndStyle | HpInputTextAndStyle
  );
  const { questionText, subtext } = textAndStyle as HpStandardInputTextAndStyle | HpFluentInputTextAndStyle;
  if (typeof questionText === 'string') {
    return { questionText, subtext };
  }

  const { buttonText, subtext: buttonSubtext } = textAndStyle as
    | HpStandardButtonTextAndStyle
    | HpCommonButtonTextAndStyle;
  if (typeof buttonText === 'string') {
    return { buttonText, subtext: buttonSubtext };
  }

  const { buttonText: uiButtonText, questionText: uiText, subtext: uiSubtext } = questionUI;
  if (uiText) {
    return { questionText: uiText, subtext: uiSubtext };
  }

  if (uiButtonText) {
    return { buttonText: uiButtonText, subtext: uiSubtext };
  }

  throw new HpError(`There is no questionText for question id ${question.questionId}.`);
}

export function evaluateDisabledExpression({
  question,
  state
}: HpEvaluateConditionalQuestionTextProps): boolean | undefined {
  const { disabledExpr, questionId } = question;
  if (!disabledExpr) {
    return;
  }

  const { answers, commonLookups, displayState, productId, questions } = state;
  const { sections } = displayState;
  const { groupNumber } = destructureControlId(questionId, { requirePrefix: false });
  const thisRef = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag: HpClientDebugFlags.DefaultAnswersAndDisabledExpressions,
    displayState,
    groupNumber,
    indexes: undefined,
    productId,
    questions,
    sections,
    sourceQuestionId: questionId,
    sourceQuestionType: getTypeOfQuestion(questionId, questions, sections)
  });

  const disabledExprValue = (disabledExpr as unknown as (this: HpTokensAndExpressionsThisReference) => boolean).call(
    thisRef
  );
  return disabledExprValue;
}

interface HpEvaluateConditionalQuestionTextProps {
  question: HpClientQuestion;
  state: HpClientQuestionSetState;
}

export function evaluateConditionalQuestionText({
  question,
  state
}: HpEvaluateConditionalQuestionTextProps): HpEither<HpQuestionTextResult, HpButtonTextResult> | undefined {
  const { data } = question;
  const { questionFormats } = state;
  const textAndStyle = selectTextAndStyleDataToUse(
    questionFormats,
    data.textAndStyle as HpButtonTextAndStyle | HpInputTextAndStyle
  );
  let { questionText } = textAndStyle as HpStandardInputTextAndStyle | HpFluentInputTextAndStyle;
  const { buttonText } = textAndStyle as HpCommonButtonTextAndStyle;
  let result: HpEither<HpQuestionTextResult, HpButtonTextResult> | undefined;
  if (!questionText && buttonText) {
    questionText = buttonText;
  }

  if (!state || !questionText || typeof questionText === 'string' || !lodash.isArray(questionText)) {
    result = undefined;
  } else {
    const { questionId } = question;
    const items: SMap<HpClientQuestionTextConditionalText[]> = { [questionId]: [] };
    let conditionMet = false;
    for (const conditionalText of questionText) {
      const { condition, subtext, text } = conditionalText;
      items[questionId].push({ condition: condition as unknown as HpClientConditionFunction, text, subtext });
      const conditionMetAction = () => {
        conditionMet = true;
        return undefined;
      };

      performActionOnConditionalModels({
        items,
        state: state as HpClientQuestionSetState,
        conditionMetAction,
        sourceQuestionId: questionId,
        type: 'question text condition',
        eventType: 'none',
        sectionGroupNumber: undefined
      });

      if (conditionMet) {
        result = !buttonText ? { questionText: text, subtext } : { buttonText: text, subtext };
        break;
      }
    }
  }

  return result;
}

export interface HpHelpInfoConditionsHaveBeenMetProps {
  helpInfo: HpHelpInfo;
  questionId: string;
  questionUI: HpClientQuestionUI;
  sectionGroupNumber?: number | undefined;
  state: HpClientQuestionSetState;
}

export function helpInfoConditionsHaveBeenMet({
  helpInfo,
  questionId,
  questionUI,
  state,
  sectionGroupNumber
}: HpHelpInfoConditionsHaveBeenMetProps): HpClientQuestionUI | undefined {
  if (!questionUI) {
    questionUI = {} as any;
  }

  const prevUI: HpHelpInfoUI = questionUI.helpInfo || {};
  const { isHelpVisible } = prevUI;
  const currUI: HpHelpInfoUI = {};
  if (isHelpVisible) {
    currUI.isHelpVisible = true;
  }

  const items: SMap<HpClientHelpInfoConditionalText[]> = {};
  const commonProps: HpProcessTextConditionalsCommonProps = {
    helpInfo,
    currUI,
    items,
    evaluateHelpInfoConditions: (conditionMetAction) => {
      return performActionOnConditionalModels({
        sourceQuestionId: questionId,
        eventType: 'none',
        items,
        conditionMetAction,
        state,
        type: 'help info',
        sectionGroupNumber
      });
    }
  };

  processTextConditionals({ textKey: 'helpText', commonProps });
  processTextConditionals({ textKey: 'infoText', commonProps });
  processTextConditionals({ textKey: 'warningText', commonProps });

  if (!areItemsEquivalent(prevUI, currUI, helpInfoUiKeys)) {
    return applyChanges(questionUI, { helpInfo: currUI });
  }

  return undefined;
}

interface HpGetHelpKeyPropsResult {
  canShowKey: string;
  conditionMapKey: string;
  infoIconHiddenKey: string | undefined;
  positionKey: string | undefined;
}

//TODO: PR - Refactor widget implementation => change the widget properties aligns with interfaces use in widgetUtilities
function getHelpKeyProps(textKey: keyof HpHelpInfo): HpGetHelpKeyPropsResult {
  let canShowKey: keyof HpHelpInfoUI;
  let positionKey: keyof HpHelpInfoUI | undefined;
  let conditionMapKey: string;
  let infoIconHiddenKey: string | undefined;

  switch (textKey) {
    case 'helpText':
      canShowKey = 'canShowHelp';
      conditionMapKey = 'help';
      break;
    case 'infoText':
      canShowKey = 'canShowInfo';
      positionKey = 'infoPosition';
      conditionMapKey = 'info';
      infoIconHiddenKey = 'isInfoIconHidden';
      break;
    case 'warningText':
      canShowKey = 'canShowWarning';
      conditionMapKey = 'warning';
      break;
    default:
      throw new HpError(`The key "${String(textKey)}" is not handled. A code change is required.`);
  }

  return { canShowKey, conditionMapKey, positionKey, infoIconHiddenKey };
}

type HpEvalateHelpInfoConditionsFunction = (
  conditionMetAction: HpConditionMetFunction<HpClientHelpInfoConditionalText>
) => HpClientQuestionSetState | undefined;

interface HpProcessTextConditionalsProps {
  commonProps: HpProcessTextConditionalsCommonProps;
  textKey: keyof HpHelpInfo;
}

interface HpProcessTextConditionalsCommonProps {
  currUI: HpHelpInfoUI;
  evaluateHelpInfoConditions: HpEvalateHelpInfoConditionsFunction;
  helpInfo: HpHelpInfo;
  items: SMap<HpClientHelpInfoConditionalText[]>;
}

function processTextConditionals({ commonProps, textKey }: HpProcessTextConditionalsProps) {
  const { currUI, evaluateHelpInfoConditions, helpInfo, items } = commonProps;
  const { canShowKey, conditionMapKey, positionKey, infoIconHiddenKey } = getHelpKeyProps(textKey);
  const itemText: HpHelpInfoItem | undefined = helpInfo[textKey];
  const itemCond = itemText as unknown as HpClientHelpInfoConditionalText;
  const isItemAnArray = lodash.isArray(itemText);
  const isItemAnObject = !isItemAnArray && typeof itemText === 'object';
  const shouldEvaluateConditions = isItemAnArray || isItemAnObject;
  if (!shouldEvaluateConditions) {
    return undefined;
  }

  currUI[canShowKey] = false;
  delete currUI[textKey];
  if (positionKey) {
    delete currUI[positionKey];
  }

  if (isItemAnArray) {
    items.help = itemText.map((item) => item as unknown as HpClientHelpInfoConditionalText);
  } else if (isItemAnObject) {
    items.help = [itemCond];
  }

  const conditionMetAction: HpConditionMetFunction<HpClientHelpInfoConditionalText> = ({ item }) => {
    currUI[canShowKey] = true;
    const { isIconHidden, position, text } = item;
    if (text) {
      currUI[textKey] = text;
      if (positionKey && position) {
        (currUI[positionKey] as HpInfoPositionType) = position;
      }

      if (infoIconHiddenKey && isIconHidden && textKey === 'infoText') {
        (currUI[infoIconHiddenKey] as boolean) = isIconHidden;
      }
    }

    return undefined;
  };

  evaluateHelpInfoConditions(conditionMetAction);
  if (textKey === 'helpText' || textKey === 'warningText') {
    delete items[conditionMapKey];
  }
}

export function areItemsEquivalent<ItemT extends Record<string, any>>(
  a: ItemT | null | undefined,
  b: ItemT | null | undefined,
  keys: (keyof ItemT)[] | null
): boolean {
  const hasA = a !== null && a !== undefined;
  const hasB = b !== null && b !== undefined;
  if (!hasA && !hasB) {
    return true;
  }

  if (hasA !== hasB) {
    return false;
  }

  if (keys === null) {
    return a === b;
  }

  let areEqual = true;
  if (a && b) {
    for (let i = 0; areEqual && i < keys.length; i++) {
      const key = keys[i];
      areEqual = areItemsEquivalent(a[key], b[key], null);
    }
  }

  return areEqual;
}

export function addErrorToValidation(
  validationResult: HpClientValidationResult,
  fieldId: string,
  error: string | undefined
): HpClientValidationResult {
  const hasError = !!validationResult.errors[fieldId];
  const removeError = hasError && !error;
  const addError = !hasError && !!error;
  const changeError = hasError && !!error;
  let result = validationResult;
  if (removeError || addError || changeError) {
    result = { ...validationResult };
    result.errors = { ...result.errors };
    if (addError || changeError) {
      result.errors[fieldId] = error as string;
      if (addError) {
        result.count++;
      }
    } else {
      delete result.errors[fieldId];
      result.count--;
    }
  }

  return result;
}

export interface HpValidateSectionProps {
  refCode?: string;
  sectionId?: string;
  state: HpClientQuestionSetState;
}

export function validateSection({
  refCode,
  sectionId: maybeSectionId,
  state
}: HpValidateSectionProps): HpClientValidationResult {
  const { displayState, questions, validationResult: validationResultOrig } = state;
  const { activeSectionId, questionsUI } = displayState;
  const sectionId = maybeSectionId || activeSectionId;
  const model = createValidationModel(state, sectionId);
  const invisibleQuestions: string[] = [];
  let validationResult = lodash.cloneDeep(validationResultOrig);
  refCode = refCode ? ` [${refCode}]` : '';

  iterateInvisibleQuestions(questions, questionsUI, sectionId, ({ questionId }) => {
    validationResult = addErrorToValidation(validationResult, questionId, undefined);
    invisibleQuestions.push(questionId);
  });

  const fieldKeysToValidate = model.getAllKeys().filter((key) => !invisibleQuestions.includes(key));
  const fieldValidation = validateFields({
    fields: model,
    fieldKeysToValidate,
    previousResult: undefined, // only provide this if we want to combine results with previous errors.
    eventType: 'section-validation'
  });

  validationResult = {
    ...validationResult,
    ...fieldValidation
  };

  logInfo(
    `Validated Section ID '${sectionId}' with ${validationResult.count} errors${refCode}.`,
    HpClientDebugFlags.ValidatorExpressions
  );

  const isValid = validationResult.count === 0;
  const { validSections } = validationResult;
  if (isValid) {
    validSections.push(sectionId);
    validSections.sort();
  } else {
    validationResult.validSections = validSections.filter((s) => s !== sectionId);
  }

  return validationResult;
}

export function createValidationFields(question: HpClientQuestion, answer: string): HpFields {
  const { questionId } = question;
  const fields: HpFields = {
    get: function (key: string): HpField {
      return {
        key,
        name: question.name,
        value: answer,
        label: question.label,
        validators: question.data.validation
      };
    },
    set: notImplemented,
    exists: (key: string) => key === questionId,
    getAllKeys: () => {
      return [questionId];
    },
    getData: notImplemented,
    getThis: () => {
      return {};
    }
  };
  return fields;
}

function notImplemented() {
  throw new Error('Not implemented [CVF].');
}

interface HpDetermineHighestSectionStartedPositionResult {
  highestSectionIdStarted: string;
  highestSectionStartedPosition: number;
}

export function determineHighestSectionStartedPosition(
  displayState: HpQuestionDisplayState,
  isJourneyComplete: boolean
): HpDetermineHighestSectionStartedPositionResult {
  const { activeSectionId, highestSectionStartedPosition, sectionIdOrder, sections, sectionsUI } = displayState;
  const activeSectionPosition = sections[activeSectionId].position;
  let newHighestPosition = 0;
  const posToIndex: SMap<number> = {};

  iterateSections({
    sectionIdOrder,
    sections,
    sectionsUI,
    action: ({ section, ui, index }) => {
      if (!ui.isVisible) {
        return;
      }

      const { position } = section;
      posToIndex[position] = index;
      if (isJourneyComplete) {
        if (position >= newHighestPosition) {
          newHighestPosition = position;
        }
      } else if (position >= activeSectionPosition) {
        if (position <= highestSectionStartedPosition && position >= activeSectionPosition) {
          newHighestPosition = position;
        }
      }
    }
  });

  const highestStartedPosition = newHighestPosition || activeSectionPosition;
  const highestSectionIdStarted = sectionIdOrder[posToIndex[highestStartedPosition]];
  return { highestSectionStartedPosition: highestStartedPosition, highestSectionIdStarted };
}

/**
 * Determines whether we can move to another section.
 * Things that will prevent moving are: validationResult errors (param).
 *
 * This does not modify state.
 * It uses current state to determinte what should be done only.
 */
export function canMoveToDesiredSection({
  buttonAction,
  bypassMoveToSectionCheck,
  currentSectionId,
  desiredSectionId,
  displayState,
  state,
  validationResult
}: HpCanMoveToDesiredSectionProps): HpCanMoveToDesiredSectionResult {
  let moveToSectionId: string | undefined;

  const { highestSectionStartedPosition, sectionIdOrder, sections, sectionsUI } = displayState;
  const { count: errorCount } = validationResult;
  const currentSectionPosition = sections[currentSectionId].position;

  if (errorCount > 0) {
    return {
      moveToSectionId: currentSectionId,
      moveToSectionPosition: currentSectionPosition,
      validationResult
    };
  }

  if (buttonAction !== 'move-to-section') {
    moveToSectionId = getNextOrPreviousSectionId(
      displayState,
      buttonAction === 'previous-section' ? 'prev' : 'next',
      currentSectionId
    );
  } else {
    desiredSectionId = ensureDefined(desiredSectionId, 'desiredSectionId');
    const desiredSectionPosition = sections[desiredSectionId].position;

    if (!bypassMoveToSectionCheck && desiredSectionPosition > highestSectionStartedPosition) {
      iterateSections({
        sectionIdOrder,
        sections,
        sectionsUI,
        action: ({ section, id, ui }) => {
          if (ui.isVisible && section.position === highestSectionStartedPosition) {
            moveToSectionId = id;
            return false;
          }
        }
      });
    } else {
      if (!validateOnBackNavigation && currentSectionPosition + 1 < desiredSectionPosition) {
        iterateSections({
          sectionIdOrder,
          sections,
          sectionsUI,
          action: ({ section, id, ui }) => {
            const position = section.position;
            if (ui.isVisible && position <= desiredSectionPosition && position > currentSectionPosition) {
              const validation = validateSection({ state, sectionId: id });
              if (validation.count) {
                moveToSectionId = id;
                validationResult = validation;
                return false;
              }
            }
          }
        });
        if (!validationResult.count) {
          moveToSectionId = desiredSectionId;
        }
      } else {
        moveToSectionId = desiredSectionId;
      }
    }
  }

  if (!moveToSectionId) {
    moveToSectionId = currentSectionId;
  }

  return {
    moveToSectionId,
    moveToSectionPosition: sections[moveToSectionId].position,
    validationResult
  };
}

export interface HpGetHighestSectionAndErrorsIfApplicableProps {
  bypassMoveToSectionCheck: true | undefined;
  displayState: HpQuestionDisplayState;
  state: HpClientQuestionSetState;
}

export interface HpGetHighestSectionAndErrorsIfApplicable {
  highestSectionStartedId: string;
  highestSectionStartedPosition: number;
  validationResult: HpClientValidationResult | undefined;
}

export function getHighestSectionAndErrorsIfApplicable({
  state,
  // bypassMoveToSectionCheck,
  displayState
}: HpGetHighestSectionAndErrorsIfApplicableProps): HpGetHighestSectionAndErrorsIfApplicable {
  const { answers, productId } = state;
  const metadata: Partial<HpMetaData> = getMetaDataOrUndefinedFromAnswers(productId, answers) || {};
  const { isJourneyComplete = false } = metadata;
  const { highestSectionIdStarted, highestSectionStartedPosition } = determineHighestSectionStartedPosition(
    displayState,
    isJourneyComplete
  );

  return {
    highestSectionStartedPosition: highestSectionStartedPosition,
    highestSectionStartedId: highestSectionIdStarted,
    validationResult: undefined
  };
}

export function selectTextAndStyleDataToUse(
  questionFormats: HpQuestionFormatTypes[],
  textAndStyle: HpButtonTextAndStyle | HpInputTextAndStyle
): HpStandardButtonTextAndStyle | HpFluentButtonTextAndStyle | HpStandardInputTextAndStyle | HpFluentInputTextAndStyle {
  const len = questionFormats.length;
  for (let i = 0; i < len; i++) {
    const formatType = questionFormats[i];
    const data = textAndStyle[formatType];
    if (data) {
      return data;
    }
  }

  throw new HpError(
    `No format could be found for the control data. Data provided: ${JSON.stringify({ questionFormats, textAndStyle })}`
  );
}

export interface HpIterateSectionsProps {
  action: (props: HpIterateSectionsActionProps) => false | void;
  sectionIdOrder: string[];
  sections: HpClientSectionMap;
  sectionsUI: HpClientSectionsUiMap;
}

export interface HpIterateSectionsActionProps {
  id: string;
  index: number;
  section: HpClientSection;
  ui: HpClientSectionUI;
}

export function iterateSections({ action, sectionIdOrder, sections, sectionsUI }: HpIterateSectionsProps): void {
  for (let ix = 0; ix < sectionIdOrder.length; ix++) {
    const id = sectionIdOrder[ix];
    const section = sections[id];
    const ui = sectionsUI[id];
    if (action({ section, ui, id, index: ix }) === false) {
      break;
    }
  }
}

export interface HpTraverseRepeatingGroupTreeSetup {
  allAnswerIds: string[];
  allQuestionIds: string[];
  allSectionIds: string[];
  destructuredMap: SMap<HpDestructuredControlIdResult>;
}

export function createSetupForTraverseRepeatingGroupTree(
  state:
    | HpClientQuestionSetState
    | {
        answers: HpClientAnswerMap | HpAnswerMap;
        questions: HpClientQuestionMap | HpQuestionMap;
        sections: HpClientSectionMap | HpSectionMap;
        displayState?: undefined;
      }
): HpTraverseRepeatingGroupTreeSetup {
  const { answers, displayState, questions } = state;
  const { sections } = displayState || state;
  const allAnswerIds = lodash.keys(answers);
  const answerCount = allAnswerIds.length;
  const destructuredMap: SMap<HpDestructuredControlIdResult> = {};
  for (let i = 0; i < answerCount; i++) {
    const answerId = allAnswerIds[i];
    destructuredMap[answerId] = destructureControlId(answerId, { requirePrefix: false });
  }

  return {
    allSectionIds: lodash.keys(sections),
    allQuestionIds: lodash.keys(questions),
    allAnswerIds,
    destructuredMap
  };
}

export interface HpTraverseRepeatingGroupsTreeProps {
  action: (answerId: string) => void;
  answers: HpAnswerMap;
  groupId: string;
  questionId: string | undefined;
  questions: HpQuestionMap | HpClientQuestionMap;
  sections: HpSectionMap | HpClientSectionMap;
  setup: HpTraverseRepeatingGroupTreeSetup;
}

export function traverseRepeatingGroupTree({
  action,
  answers,
  groupId,
  questionId,
  questions,
  sections,
  setup
}: HpTraverseRepeatingGroupsTreeProps): void {
  const { allSectionIds } = setup;
  const { answerIds } = determineHighestGroupNumberInSection({
    groupId,
    sections,
    questions,
    questionId,
    setup
  });

  for (const answerId of answerIds) {
    action(answerId);
  }

  const sectionIds = allSectionIds
    .map((sectionId) => {
      const child = sections[sectionId];
      return child && child.parentId === groupId ? sectionId : null;
    })
    .filter((c) => c !== null) as string[];

  for (const sectionId of sectionIds) {
    traverseRepeatingGroupTree({
      action,
      groupId: sectionId,
      answers,
      questions,
      sections,
      questionId: undefined,
      setup
    });
  }
}

interface HpDetermineHighestGroupNumberInSectionResult {
  answerIds: string[];
  highestGroupNumber: number;
}

interface HpDetermineHighestGroupNumberInSectionProps {
  groupId: string;
  questionId: string | undefined;
  questions: HpQuestionMap | HpClientQuestionMap;
  sections: HpSectionMap | HpClientSectionMap;
  setup: HpTraverseRepeatingGroupTreeSetup;
}

function determineHighestGroupNumberInSection({
  groupId,
  questionId,
  questions,
  sections,
  setup
}: HpDetermineHighestGroupNumberInSectionProps): HpDetermineHighestGroupNumberInSectionResult {
  const { allAnswerIds, allQuestionIds, destructuredMap } = setup;
  const section = sections[groupId];
  let questionIds: string[] | undefined;
  if (questionId) {
    questionIds = [questionId];
  } else {
    ({ questionIds } = section);
    if (!questionIds || questionIds.length === 0) {
      questionIds = [];
      for (const id of allQuestionIds) {
        const q = questions[id];
        if (q.groupId === groupId) {
          questionIds.push(id);
        }
      }
    }
  }

  const questionIdMap: SMap<true> = {};
  for (const id of questionIds) {
    questionIdMap[id] = true;
  }

  let highestGroupNumber = -1;
  const answerIds: string[] = [];
  allAnswerIds.forEach((id: string) => {
    const { questionId, groupNumber } = destructuredMap[id];
    // groupNumber will never be undefined unless something has gone very wrong
    if (groupNumber !== undefined && questionIdMap[questionId]) {
      answerIds.push(id);
      if (groupNumber > highestGroupNumber) {
        highestGroupNumber = groupNumber;
      }
    }
  });

  return { highestGroupNumber, answerIds };
}

export function getTypeOfQuestion(
  questionId: string,
  questions: SMap<HpQuestionBasic>,
  sections: SMap<HpSectionBasic>
): HpQuestionType {
  return questions[questionId] ? 'question' : sections[questionId] ? 'section' : undefined;
}
